import { defineStore } from 'pinia';
import { updateProfile } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, getDocFromServer, limit, query, setDoc, updateDoc } from 'firebase/firestore';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { normalizeNotificationPreferences } from '~/lib/notifications';
import { workspaceTourProgressKey } from '~/lib/workspaceTour';
import type { User, UserAppearancePreferences, UserGuidedTourProgress, UserIntegrations, UserMyHubPreferences, UserTaskPreferences } from '~/types/models';
import { IDENTITY_DATA_MAX_AGE_MS, runFreshRequest, type FreshRequestOptions } from '~/lib/requestFreshness';
import { publicImmutableUploadMetadata } from '~/lib/storageUploadMetadata';

type UserAccessSummary = {
    hasTeamAccess: boolean,
    hasClientAccess: boolean,
    defaultContext: 'team' | 'client' | 'onboarding',
};

export const useUserStore = defineStore('user', {
    state: () => ({
        user: null as User | null,
        accessSummary: {
            hasTeamAccess: false,
            hasClientAccess: false,
            defaultContext: 'onboarding',
        } as UserAccessSummary,
        loading: false,
        error: null as string | null,
        loadedForUserId: null as string | null,
    }),

    actions: {
        /**
         * Fetches the current user from Firestore
         */
        async fetchUser(userId: string, options: FreshRequestOptions = {}): Promise<boolean> {
            const normalizedUserId = String(userId || '').trim();
            const result = await runFreshRequest(
                `user:${normalizedUserId}`,
                () => this._fetchUserFromFirestore(normalizedUserId),
                {
                    maxAgeMs: IDENTITY_DATA_MAX_AGE_MS,
                    ...options,
                    hasData: this.loadedForUserId === normalizedUserId && this.user?.id === normalizedUserId,
                },
            );
            return result ?? true;
        },

        async _fetchUserFromFirestore(userId: string): Promise<boolean> {
            const { $db, $auth } = useNuxtApp();
            this.loading = true;
            this.error = null;

            try {
                const userDocRef = doc($db, 'users', userId);
                const userSnap = await getDocFromServer(userDocRef).catch(async(serverError) => {
                    console.warn('[userStore] Falling back to cached user profile read:', serverError);
                    return await getDoc(userDocRef);
                });
                const portalAccessRef = collection($db, `users/${userId}/portalAccess`);
                const portalAccessSnap = await getDocs(query(portalAccessRef, limit(1)));
                const fallbackEmail = String($auth.currentUser?.email || '').trim().toLowerCase();
                const tenantStore = useTenantStore();

                const hydrateAccessSummary = (tenantEntries: Array<{ tenantId: string; role: string }>) => {
                    const hasTeamAccess = tenantEntries.length > 0;
                    const hasClientAccess = !portalAccessSnap.empty;

                    this.accessSummary = {
                        hasTeamAccess,
                        hasClientAccess,
                        defaultContext: hasTeamAccess ? 'team' : hasClientAccess ? 'client' : 'onboarding',
                    };

                    return { hasTeamAccess, hasClientAccess };
                };

                if (userSnap.exists()) {
                    const userData = userSnap.data() as User & { tenants?: Array<{ tenantId: string; role: string }> };
                    let tenantEntries = Array.isArray(userData.tenants) ? userData.tenants : [];

                    if (tenantEntries.length === 0) {
                        const recoveredTenants = await tenantStore.recoverTenantsFromMembership(userId, fallbackEmail);
                        if (recoveredTenants.length > 0) {
                            tenantEntries = recoveredTenants;

                            try {
                                await updateDoc(userDocRef, {
                                    tenants: recoveredTenants,
                                    updatedAt: new Date(),
                                });
                            } catch (persistError) {
                                console.warn('Unable to persist recovered team access on user profile:', persistError);
                            }
                        }
                    }

                    const { hasTeamAccess, hasClientAccess } = hydrateAccessSummary(tenantEntries);
                    const normalizedPreferences = userData.preferences
                        ? {
                            ...userData.preferences,
                            notifications: normalizeNotificationPreferences(userData.preferences.notifications || null),
                        }
                        : undefined;

                    this.user = {
                        ...userData,
                        id: userSnap.id,
                        tenants: tenantEntries,
                        ...(normalizedPreferences ? { preferences: normalizedPreferences } : {}),
                        accountType: hasTeamAccess && hasClientAccess
                            ? undefined
                            : hasTeamAccess
                                ? 'team'
                                : hasClientAccess
                                    ? 'client'
                                    : userData.accountType,
                    } as User;
                } else {
                    const recoveredTenants = await tenantStore.recoverTenantsFromMembership(userId, fallbackEmail);
                    const { hasTeamAccess, hasClientAccess } = hydrateAccessSummary(recoveredTenants);

                    if (hasTeamAccess || hasClientAccess) {
                        const nextUser: User = {
                            id: userId,
                            email: fallbackEmail,
                            displayName: String($auth.currentUser?.displayName || '').trim(),
                            fullName: String($auth.currentUser?.displayName || '').trim(),
                            avatarURL: String($auth.currentUser?.photoURL || '').trim(),
                            tenants: recoveredTenants,
                            accountType: hasTeamAccess && hasClientAccess
                                ? undefined
                                : hasTeamAccess
                                    ? 'team'
                                    : hasClientAccess
                                        ? 'client'
                                        : undefined,
                            createdAt: new Date(),
                            updatedAt: new Date(),
                        } as User;

                        this.user = nextUser;

                        try {
                            await setDoc(userDocRef, {
                                email: nextUser.email,
                                displayName: nextUser.displayName,
                                fullName: nextUser.fullName,
                                avatarURL: nextUser.avatarURL,
                                tenants: recoveredTenants,
                                ...(nextUser.accountType ? { accountType: nextUser.accountType } : {}),
                                createdAt: new Date(),
                                updatedAt: new Date(),
                            }, { merge: true });
                        } catch (persistError) {
                            console.warn('Unable to create recovered user profile:', persistError);
                        }
                    } else {
                        this.accessSummary = {
                            hasTeamAccess: false,
                            hasClientAccess: false,
                            defaultContext: 'onboarding',
                        };
                        this.error = 'User not found';
                    }
                }
                this.loadedForUserId = userId;
                return true;
            } catch (error) {
                this.error = `Error fetching user: ${error}`;
                console.error(this.error);
                return false;
            } finally {
                this.loading = false;
            }
        },

        async updateAvatar(file: File): Promise<string> {
            const { $auth, $db, $storage } = useNuxtApp();
            const currentUser = $auth.currentUser;

            if (!currentUser?.uid) {
                throw new Error('You must be logged in to update your avatar.');
            }

            const userId = currentUser.uid;
            const filename = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
            const avatarRef = storageRef($storage, `users/${userId}/avatar/${Date.now()}-${filename}`);

            await uploadBytes(avatarRef, file, publicImmutableUploadMetadata(file));
            const avatarURL = await getDownloadURL(avatarRef);

            await updateDoc(doc($db, 'users', userId), {
                avatarURL,
                updatedAt: new Date(),
            });

            await updateProfile(currentUser, {
                photoURL: avatarURL,
            });

            if (this.user?.id === userId) {
                this.user = {
                    ...this.user,
                    avatarURL,
                    updatedAt: new Date(),
                };
            }

            return avatarURL;
        },

        async updateMyHubPreferences(myHub: UserMyHubPreferences): Promise<void> {
            const { $auth, $db } = useNuxtApp();
            const currentUser = $auth.currentUser;
            const userId = currentUser?.uid || this.user?.id;

            if (!userId) {
                throw new Error('You must be logged in to customize My Hub.');
            }

            const updatedAt = new Date();
            const preferences = {
                ...(this.user?.preferences || {}),
                myHub: {
                    ...myHub,
                    updatedAt,
                },
            };

            await updateDoc(doc($db, 'users', userId), {
                preferences,
                updatedAt,
            });

            if (this.user?.id === userId) {
                this.user = {
                    ...this.user,
                    preferences,
                    updatedAt,
                };
            }
        },

        async updateAppearancePreferences(appearance: UserAppearancePreferences): Promise<void> {
            const { $auth, $db } = useNuxtApp();
            const currentUser = $auth.currentUser;
            const userId = currentUser?.uid || this.user?.id;

            if (!userId) {
                throw new Error('You must be logged in to update appearance preferences.');
            }
            if (!$db) {
                throw new Error('Appearance preferences are unavailable until the workspace finishes loading.');
            }

            const updatedAt = new Date();
            const preferences = {
                ...(this.user?.preferences || {}),
                appearance: {
                    ...(this.user?.preferences?.appearance || {}),
                    ...appearance,
                    updatedAt,
                },
            };
            const userDocRef = doc($db, 'users', userId);

            await setDoc(userDocRef, {
                email: String(currentUser?.email || this.user?.email || '').trim().toLowerCase(),
                displayName: String(currentUser?.displayName || this.user?.displayName || '').trim(),
                fullName: String(this.user?.fullName || currentUser?.displayName || '').trim(),
                avatarURL: String(currentUser?.photoURL || this.user?.avatarURL || '').trim(),
                tenants: this.user?.tenants || [],
                ...(this.user?.accountType ? { accountType: this.user.accountType } : {}),
                preferences,
                updatedAt,
            }, { merge: true });

            this.user = {
                ...(this.user || {
                    id: userId,
                    email: String(currentUser?.email || '').trim().toLowerCase(),
                    displayName: String(currentUser?.displayName || '').trim(),
                    fullName: String(currentUser?.displayName || '').trim(),
                    avatarURL: String(currentUser?.photoURL || '').trim(),
                    createdAt: updatedAt,
                }),
                preferences,
                updatedAt,
            } as User;
        },

        async updateGuidedTourProgress(
            tenantId: string,
            tourId: string,
            progress: Omit<UserGuidedTourProgress, 'updatedAt'>,
        ): Promise<void> {
            const { $auth, $db } = useNuxtApp();
            const currentUser = $auth.currentUser;
            const userId = currentUser?.uid || this.user?.id;
            const progressKey = workspaceTourProgressKey(tenantId, tourId);

            if (!userId) {
                throw new Error('You must be logged in to save tour progress.');
            }
            if (!tenantId || !tourId) {
                throw new Error('Tour progress needs a workspace and tour identifier.');
            }

            const updatedAt = new Date();
            const preferences = {
                ...(this.user?.preferences || {}),
                guidedTours: {
                    ...(this.user?.preferences?.guidedTours || {}),
                    [progressKey]: {
                        ...progress,
                        updatedAt,
                    },
                },
            };

            await setDoc(doc($db, 'users', userId), {
                preferences,
                updatedAt,
            }, { merge: true });

            if (this.user?.id === userId) {
                this.user = {
                    ...this.user,
                    preferences,
                    updatedAt,
                };
            }
        },

        async updateTaskPreferences(tasks: UserTaskPreferences): Promise<void> {
            const { $auth, $db } = useNuxtApp();
            const currentUser = $auth.currentUser;
            const userId = currentUser?.uid || this.user?.id;

            if (!userId) {
                throw new Error('You must be logged in to update task preferences.');
            }

            const updatedAt = new Date();
            const inferredAccountType = this.user?.accountType
                || (this.accessSummary.hasClientAccess && !this.accessSummary.hasTeamAccess ? 'client' : 'team');
            const preferences = {
                ...(this.user?.preferences || {}),
                tasks: {
                    ...(this.user?.preferences?.tasks || {}),
                    ...tasks,
                    updatedAt,
                },
            };
            const userDocRef = doc($db, 'users', userId);

            try {
                await updateDoc(userDocRef, {
                    preferences,
                    updatedAt,
                });
            } catch (error: any) {
                const message = String(error?.message || '');
                const code = String(error?.code || '');
                const missingDocument = code === 'not-found'
                    || message.toLowerCase().includes('no document to update');

                if (!missingDocument) {
                    throw error;
                }

                await setDoc(userDocRef, {
                    email: String(currentUser?.email || this.user?.email || '').trim().toLowerCase(),
                    displayName: String(currentUser?.displayName || this.user?.displayName || '').trim(),
                    fullName: String(this.user?.fullName || currentUser?.displayName || '').trim(),
                    avatarURL: String(currentUser?.photoURL || this.user?.avatarURL || '').trim(),
                    tenants: this.user?.tenants || [],
                    accountType: inferredAccountType,
                    preferences,
                    createdAt: updatedAt,
                    updatedAt,
                }, { merge: true });
            }

            const baseUser = this.user?.id === userId
                ? this.user
                : {
                    id: userId,
                    email: String(currentUser?.email || this.user?.email || '').trim().toLowerCase(),
                    displayName: String(currentUser?.displayName || this.user?.displayName || '').trim(),
                    fullName: String(this.user?.fullName || currentUser?.displayName || '').trim(),
                    avatarURL: String(currentUser?.photoURL || this.user?.avatarURL || '').trim(),
                    tenants: this.user?.tenants || [],
                    accountType: inferredAccountType,
                    createdAt: this.user?.createdAt || updatedAt,
                    updatedAt,
                };

            this.user = {
                ...baseUser,
                preferences,
                updatedAt,
            } as User;
        },

        async updateIntegrations(integrations: UserIntegrations): Promise<void> {
            const { $auth, $db } = useNuxtApp();
            const currentUser = $auth.currentUser;
            const userId = currentUser?.uid || this.user?.id;

            if (!userId) {
                throw new Error('You must be logged in to update integrations.');
            }

            const updatedAt = new Date();
            const nextIntegrations = {
                ...(this.user?.integrations || {}),
                ...integrations,
            };

            await updateDoc(doc($db, 'users', userId), {
                integrations: nextIntegrations,
                updatedAt,
            });

            if (this.user?.id === userId) {
                this.user = {
                    ...this.user,
                    integrations: nextIntegrations,
                    updatedAt,
                };
            }
        },

        /**
         * Clears user data (on logout)
         */
        clearUser(): void {
            this.user = null;
            this.loadedForUserId = null;
            this.accessSummary = {
                hasTeamAccess: false,
                hasClientAccess: false,
                defaultContext: 'onboarding',
            };
        },
    },

    getters: {
        isLoggedIn: (state) => !!state.user,
        userId: (state) => state.user?.id,
        hasTeamAccess: (state) => state.accessSummary.hasTeamAccess,
        hasClientAccess: (state) => state.accessSummary.hasClientAccess,
        defaultAccessContext: (state) => state.accessSummary.defaultContext,
    },
});
